import React, { useState } from "react";
import { Send, X, Plus } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

type Invite = {
  id: string;
  email: string;
  name: string;
};

interface AddSupplierInvitesProps {
  rfpId: string;
  onInvitesSent?: () => void;
}

export const AddSupplierInvites = ({ rfpId, onInvitesSent }: AddSupplierInvitesProps) => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [invites, setInvites] = useState<Invite[]>([]);
  const [sending, setSending] = useState(false);
  const { toast } = useToast();

  const addInvite = () => {
    if (!email || !name) {
      toast({
        title: "Missing Information",
        description: "Please enter both email and supplier name.",
        variant: "destructive"
      });
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast({
        title: "Invalid Email",
        description: "Please enter a valid email address.",
        variant: "destructive"
      });
      return;
    }

    if (invites.some(inv => inv.email === email)) {
      toast({
        title: "Duplicate Email",
        description: "This email has already been added.",
        variant: "destructive"
      });
      return;
    }

    const newInvite: Invite = {
      id: Date.now().toString(),
      email: email.trim(),
      name: name.trim(),
    };

    setInvites([...invites, newInvite]);
    setEmail("");
    setName("");
  };

  const removeInvite = (id: string) => {
    setInvites(invites.filter(invite => invite.id !== id));
  };

  const sendInvites = async () => {
    if (invites.length === 0) {
      toast({
        title: "No Invites",
        description: "Please add at least one invite before sending.",
        variant: "destructive"
      });
      return;
    }

    setSending(true);
    try {
      // Get authenticated user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error("Not authenticated");
      }

      // Fetch RFP details
      const { data: rfpData, error: rfpError } = await supabase
        .from("rfps")
        .select("*")
        .eq("id", rfpId)
        .single();

      if (rfpError) throw rfpError;

      // Create supplier invitations in database
      const invitations = invites.map(invite => ({
        rfp_id: rfpId,
        supplier_name: invite.name,
        supplier_email: invite.email,
        invite_token: crypto.randomUUID(),
      }));

      const { data: createdInvitations, error: invitationsError } = await supabase
        .from("supplier_invitations")
        .insert(invitations)
        .select();

      if (invitationsError) throw invitationsError;

      // Get buyer profile
      const { data: profile } = await supabase
        .from("profiles")
        .select("name")
        .eq("id", user.id)
        .single();

      // Send invitation emails - pass the current app URL
      const appUrl = window.location.origin;
      const { error: emailError } = await supabase.functions.invoke("send-rfp-invitations", {
        body: {
          rfpId: rfpId,
          appUrl: appUrl,
          invitations: createdInvitations.map(inv => ({
            id: inv.id,
            supplierName: inv.supplier_name,
            supplierEmail: inv.supplier_email,
            inviteToken: inv.invite_token,
          })),
          rfpTitle: rfpData.title,
          rfpDescription: rfpData.description,
          submissionDeadline: rfpData.submission_deadline,
          auctionStart: rfpData.auction_start,
          auctionEnd: rfpData.auction_end,
          buyerName: profile?.name || "A buyer",
        },
      });

      if (emailError) {
        console.error("Email error:", emailError);
        toast({
          title: "Warning",
          description: "Invitations created but some emails may not have been sent",
        });
      } else {
        toast({
          title: "Invites Sent",
          description: `${invites.length} invitation(s) sent successfully!`,
        });
      }

      setInvites([]);
      onInvitesSent?.();
    } catch (error: any) {
      console.error("Error sending invites:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to send invitations. Please try again.",
        variant: "destructive"
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Invite Additional Suppliers</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="supplier-name">Supplier Name</Label>
            <Input
              id="supplier-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter supplier company name"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="supplier-email">Email Address</Label>
            <Input
              id="supplier-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="supplier@example.com"
            />
          </div>

          <Button
            type="button"
            onClick={addInvite}
            variant="outline"
            className="w-full"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add to List
          </Button>
        </div>

        {invites.length > 0 && (
          <div className="space-y-2">
            <Label>Pending Invites ({invites.length})</Label>
            <div className="space-y-2">
              {invites.map((invite) => (
                <div
                  key={invite.id}
                  className="flex items-center justify-between p-3 bg-muted rounded-lg"
                >
                  <div className="flex-1">
                    <p className="font-medium">{invite.name}</p>
                    <p className="text-sm text-muted-foreground">{invite.email}</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeInvite(invite.id)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        <Button
          onClick={sendInvites}
          disabled={invites.length === 0 || sending}
          className="w-full"
        >
          <Send className="h-4 w-4 mr-2" />
          {sending ? "Sending..." : `Send ${invites.length} Invitation${invites.length !== 1 ? 's' : ''}`}
        </Button>
      </CardContent>
    </Card>
  );
};
